/**
 * @file     neighbourhoodScores.ts
 * @purpose  Priority scoring matrix for Greater Vancouver neighbourhoods, maps each region to scores (0-10) for lifestyle priorities.
 */

/**
 * Score each neighbourhood 0–10 for each priority dimension.
 *
 * Data sources & methodology (researched April 2026):
 *   short-commute , typical driving/transit time to downtown Vancouver
 *                    10 = <15 min, 8 = 15-25, 6 = 25-35, 4 = 35-50, 2 = 50-70, 1 = 70+
 *   good-schools  , Fraser Institute school report cards; concentration of top-quartile schools
 *   nightlife     , restaurant/bar/entertainment venue density (Yelp, Google Maps cluster data)
 *   parks         , regional/provincial park access, trail km, waterfront (Metro Van parks data)
 *   transit       , SkyTrain stations, RapidBus, bus frequency, TransLink service level
 *                    (Expo, Millennium, Canada, Evergreen lines; West Coast Express)
 *   affordability , inverse of median home price vs Metro Van benchmark (~$1.1M)
 *                    (BC Assessment 2025 roll, REBGV/FVREB MLS stats)
 *   family-friendly, % families w/ children (StatsCan 2021), rec centres, crime rate, programming
 *   walkability   , Walk Score for primary commercial/residential centre of each municipality
 */
export const NEIGHBOURHOOD_SCORES: Record<string, Record<string, number>> = {
  // Vancouver, 0 km to downtown. Walk Score 80. Canada Line + Expo + Millennium.
  // 230+ parks incl Stanley Park, Pacific Spirit. Median detached ~$2.1M (affordability: low).
  // Fraser Institute: many top-ranked schools (UHill, Lord Byng, York House, etc).
  // Gastown, Granville, Davie, Main St = peak nightlife. Lower family % than suburbs.
  'Vancouver': {
    'short-commute': 9, 'good-schools': 8, 'nightlife': 10, 'parks': 8,
    'transit': 10, 'affordability': 2, 'family-friendly': 6, 'walkability': 9,
  },
  // West Vancouver, 20-35 min to downtown via Lions Gate. No SkyTrain; bus #250/257.
  // Walk Score ~45 (Ambleside higher ~55). Median detached ~$3.2M (least affordable).
  // Fraser Institute: Collingwood, Mulgrave, West Van Secondary all top-ranked.
  // Lighthouse Park, Cypress Provincial Park, Whytecliff. Low nightlife.
  'West Van': {
    'short-commute': 4, 'good-schools': 9, 'nightlife': 3, 'parks': 9,
    'transit': 3, 'affordability': 1, 'family-friendly': 8, 'walkability': 5,
  },
  // North Van District, 30-45 min to downtown. Bus + SeaBus. No SkyTrain yet.
  // Lynn Valley, Deep Cove, Grouse Mtn, Baden Powell trail. Walk Score ~35-50.
  // Fraser Institute: solid schools (Handsworth, Carson Graham). Median ~$1.8M.
  // Very family-oriented; high % families with children (census).
  'North Van District': {
    'short-commute': 5, 'good-schools': 8, 'nightlife': 3, 'parks': 10,
    'transit': 4, 'affordability': 3, 'family-friendly': 9, 'walkability': 5,
  },
  // North Van City, 25-35 min via SeaBus + bus. Lonsdale Quay area Walk Score ~75.
  // Shipyards district has growing restaurant/bar scene. SeaBus = frequent transit.
  // Median condo/townhome more accessible but detached ~$1.7M. Good schools.
  'North Van City': {
    'short-commute': 6, 'good-schools': 7, 'nightlife': 6, 'parks': 7,
    'transit': 7, 'affordability': 4, 'family-friendly': 7, 'walkability': 8,
  },
  // Burnaby, 20-30 min. 4 SkyTrain stations (Metrotown, Brentwood, Lougheed, Edmonds+).
  // Metrotown Walk Score ~85; city average ~60. Burnaby Mountain, Deer Lake, Central Park.
  // Fraser Institute: Burnaby North/South solid. Median detached ~$1.8M.
  // Strong family demographics. Metrotown has decent dining but limited bar scene.
  'Burnaby': {
    'short-commute': 7, 'good-schools': 7, 'nightlife': 5, 'parks': 7,
    'transit': 8, 'affordability': 4, 'family-friendly': 8, 'walkability': 6,
  },
  // Richmond, 25-35 min. Canada Line (5 stations: Bridgeport to Richmond-Brighouse).
  // Walk Score ~55 (city centre higher ~65). Strong Asian dining scene but limited nightlife bars.
  // Median detached ~$1.7M. Terra Nova, Steveston waterfront, dike trails = decent parks.
  // Fraser Institute: Richmond schools consistently above provincial average.
  'Richmond': {
    'short-commute': 6, 'good-schools': 7, 'nightlife': 5, 'parks': 6,
    'transit': 7, 'affordability': 4, 'family-friendly': 8, 'walkability': 6,
  },
  // Surrey, 40-55 min from most areas. Expo Line serves north Surrey (4 stations).
  // Walk Score ~40 (Newton/Guildford/Fleetwood low; Surrey Central higher ~65).
  // Median detached ~$1.4M, more affordable. Fraser Institute: variable quality.
  // Bear Creek, Green Timbers, Crescent Beach. Large family population.
  'Surrey': {
    'short-commute': 4, 'good-schools': 5, 'nightlife': 4, 'parks': 6,
    'transit': 6, 'affordability': 7, 'family-friendly': 8, 'walkability': 4,
  },
  // Coquitlam, 35-45 min. Evergreen Extension: 3 stations (Coquitlam Central, Lincoln,
  // Lafarge Lake-Douglas). Walk Score ~50. Minnekhada, Pinecone Burke, Coquitlam River.
  // Fraser Institute: several strong schools (Pinetree, Gleneagle). Median ~$1.5M.
  'Coquitlam': {
    'short-commute': 5, 'good-schools': 7, 'nightlife': 3, 'parks': 8,
    'transit': 7, 'affordability': 6, 'family-friendly': 9, 'walkability': 5,
  },
  // Port Coquitlam, 40-50 min. No SkyTrain (bus to Coquitlam Central or WCE).
  // Walk Score ~45. Gates Park, Colony Farm, Traboulay PoCo Trail (25 km loop).
  // Median ~$1.2M, more affordable. Solid family community. Limited nightlife.
  'Port Coquitlam': {
    'short-commute': 4, 'good-schools': 6, 'nightlife': 2, 'parks': 7,
    'transit': 5, 'affordability': 7, 'family-friendly': 8, 'walkability': 4,
  },
  // Port Moody, 35-45 min. Evergreen line: Moody Centre + Inlet Centre stations.
  // Walk Score ~55. Rocky Point Park, Buntzen Lake, Sasamat Lake, inlet trail system.
  // Brewery Row (Yellow Dog, Twin Sails) = modest nightlife. Median ~$1.3M.
  // Strong family demographics; highly rated for liveability.
  'Port Moody': {
    'short-commute': 5, 'good-schools': 7, 'nightlife': 3, 'parks': 9,
    'transit': 7, 'affordability': 5, 'family-friendly': 9, 'walkability': 6,
  },
  // New Westminster, 25-30 min. Expo Line: 4 stations (New West, Columbia, 22nd, Sapperton).
  // Walk Score ~78 (downtown/Columbia area). Queens Park. Median ~$1.0M (condos lower).
  // Fraser Institute: average. Growing restaurant scene on Columbia St. Good family services.
  'New Westminster': {
    'short-commute': 7, 'good-schools': 6, 'nightlife': 5, 'parks': 6,
    'transit': 9, 'affordability': 6, 'family-friendly': 7, 'walkability': 8,
  },
  // Delta (Ladner, Tsawwassen, North Delta), 35-55 min. No SkyTrain; buses to Scott Road
  // or Bridgeport. Walk Score ~25-35. Burns Bog, Boundary Bay, Deas Island.
  // Median ~$1.3M. Strong family character. Very limited nightlife.
  'Delta': {
    'short-commute': 3, 'good-schools': 6, 'nightlife': 2, 'parks': 7,
    'transit': 3, 'affordability': 6, 'family-friendly': 8, 'walkability': 3,
  },
  // Pitt Meadows, 45-60 min. West Coast Express (peak only) + infrequent bus.
  // Walk Score ~30. Pitt Meadows Regional Airport area, dike trails, Pitt River.
  // Median ~$1.0M. Rural feel, strong family orientation. No nightlife.
  'Pitt Meadows': {
    'short-commute': 3, 'good-schools': 5, 'nightlife': 1, 'parks': 8,
    'transit': 4, 'affordability': 7, 'family-friendly': 8, 'walkability': 3,
  },
  // Langley City, 50-65 min. No SkyTrain yet (extension under construction).
  // Walk Score ~65 (downtown is compact and walkable). Limited parks within city limits.
  // Median ~$850K (more affordable). Fraser Institute: average. Small-town nightlife.
  'Langley city': {
    'short-commute': 2, 'good-schools': 6, 'nightlife': 3, 'parks': 5,
    'transit': 4, 'affordability': 8, 'family-friendly': 7, 'walkability': 6,
  },
  // Langley Township, 55-70 min. Bus only; very car-dependent. Walk Score ~20-30.
  // Campbell Valley, Derby Reach, Fort Langley trails. Median ~$1.3M (acreages higher).
  // Fraser Institute: decent schools (Langley Fine Arts, Brookswood). Horse country, farms.
  'Langley district': {
    'short-commute': 2, 'good-schools': 6, 'nightlife': 2, 'parks': 7,
    'transit': 2, 'affordability': 7, 'family-friendly': 9, 'walkability': 3,
  },
  // Bowen Island, 70+ min (20 min ferry + drive to Horseshoe Bay + 30 min to downtown).
  // No transit on island. Walk Score ~15. Crippen Park, extensive trail network.
  // Median ~$1.3M (island premium). One school (K-12). Tiny community; no nightlife.
  'Bowen Island': {
    'short-commute': 1, 'good-schools': 4, 'nightlife': 1, 'parks': 10,
    'transit': 1, 'affordability': 3, 'family-friendly': 6, 'walkability': 3,
  },
  // Lions Bay, 30-40 min drive (Hwy 99, no transit alternatives). Walk Score ~10.
  // Stunning mountain/ocean setting. Brunswick Beach, Kelvin Grove trails.
  // Median ~$1.8M (limited supply, premium). One small school. Zero nightlife.
  'Lions Bay': {
    'short-commute': 3, 'good-schools': 4, 'nightlife': 1, 'parks': 10,
    'transit': 1, 'affordability': 2, 'family-friendly': 5, 'walkability': 2,
  },
  // UBC / University Endowment Lands, 30-40 min by bus (R4 RapidBus, #14, #4).
  // No SkyTrain. Walk Score ~65 (campus core). Pacific Spirit Park (763 ha), Wreck Beach.
  // UBC = top-ranked institution; local schools (UHill Secondary) = Fraser Institute top 10.
  // Median ~$2.5M+ (Wesbrook, UEL). Student pubs but limited true nightlife.
  'UBC': {
    'short-commute': 6, 'good-schools': 10, 'nightlife': 4, 'parks': 9,
    'transit': 6, 'affordability': 2, 'family-friendly': 6, 'walkability': 7,
  },
  // Anmore, 40-50 min. No SkyTrain; bus to Coquitlam then transfer. Walk Score ~5.
  // Buntzen Lake, Belcarra Regional Park border. Large-lot rural zoning.
  // Median ~$1.8M (large lots). Feeds into Port Moody/Coquitlam schools. No nightlife.
  'Anmore': {
    'short-commute': 3, 'good-schools': 5, 'nightlife': 1, 'parks': 10,
    'transit': 2, 'affordability': 3, 'family-friendly': 7, 'walkability': 2,
  },
  // Belcarra, 45-55 min. Virtually no transit. Walk Score ~5.
  // Belcarra Regional Park, Sasamat Lake, shoreline trails. Population ~700.
  // Median ~$1.5M. One of the smallest municipalities in BC. No commercial services.
  'Belcarra': {
    'short-commute': 3, 'good-schools': 4, 'nightlife': 1, 'parks': 10,
    'transit': 1, 'affordability': 3, 'family-friendly': 6, 'walkability': 2,
  },
  // Maple Ridge, 55-70 min. West Coast Express (peak only); otherwise bus.
  // Walk Score ~35. Golden Ears Provincial Park, Alouette Lake, Kanaka Creek.
  // Median ~$1.0M, very affordable for detached. Strong family community.
  'Maple Ridge': {
    'short-commute': 2, 'good-schools': 6, 'nightlife': 2, 'parks': 9,
    'transit': 2, 'affordability': 8, 'family-friendly': 9, 'walkability': 3,
  },
  // White Rock, 50-60 min. Bus only (#351 to Bridgeport, transfer). Walk Score ~68.
  // White Rock Beach promenade, Semiahmoo Bay. Median ~$1.5M (waterfront higher).
  // Retirement/family mix. Restaurant row on Marine Dr. Fraser Institute: average.
  'White Rock': {
    'short-commute': 3, 'good-schools': 6, 'nightlife': 4, 'parks': 7,
    'transit': 3, 'affordability': 4, 'family-friendly': 7, 'walkability': 7,
  },
  // Electoral District A, unincorporated rural (UBC excluded). Indian Arm, Pitt Lake area.
  // No transit. No schools. No services. Wilderness access only. Some recreational properties.
  'Electoral district A': {
    'short-commute': 1, 'good-schools': 1, 'nightlife': 1, 'parks': 10,
    'transit': 1, 'affordability': 4, 'family-friendly': 2, 'walkability': 1,
  },
  // Barnston Island, Fraser River island, 5-min ferry from Port Kells (Surrey).
  // No transit. Agricultural land. Walk Score ~0. No schools, no commercial.
  // Affordable acreage relative to metro but very limited market.
  'Barnston island': {
    'short-commute': 1, 'good-schools': 1, 'nightlife': 1, 'parks': 7,
    'transit': 1, 'affordability': 6, 'family-friendly': 3, 'walkability': 1,
  },
};

/** Score a neighbourhood by summing its scores for selected priorities */
export function scoreNeighbourhood(regionKey: string, priorities: string[]): number {
  const scores = NEIGHBOURHOOD_SCORES[regionKey];
  if (!scores) return 0;
  return priorities.reduce((sum, p) => sum + (scores[p] ?? 0), 0);
}

/** Sort region keys by total priority score descending */
export function sortRegionsByPriorities(regionKeys: string[], priorities: string[]): string[] {
  if (priorities.length === 0) return regionKeys;
  return [...regionKeys].sort((a, b) => scoreNeighbourhood(b, priorities) - scoreNeighbourhood(a, priorities));
}
